import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import {
  emptyParameterInfo,
  extractActiveParamKeys,
  IParameterInfo,
  IParameterOption,
} from '~/src/dash/components/forms/formBuilder/common';
import { FormBuilderContext } from '~/src/dash/components/forms/formBuilder/contexts';
import { ParameterRow } from '~/src/dash/components/forms/formBuilder/ParameterRow';
import { Row, Rows, spacing, TextButton } from '~/src/dash/publicaUI';
import { useBaseStore } from '~/src/dash/views/base/contexts';

const getInitialParameters = (
  initialParameterKeys: string[],
  parameterOptions: IParameterOption[],
  cfg: Record<string, IParameterInfo>,
): IParameterInfo[] => {
  if (isEmpty(initialParameterKeys)) return [];
  const parameters: IParameterInfo[] = [];
  const possibleKeys = parameterOptions.map((option) => {
    return option.key;
  });
  initialParameterKeys.forEach((key) => {
    if (!isEmpty(cfg[key]) && possibleKeys.includes(key)) {
      parameters.push(cfg[key]);
    }
  });
  return parameters;
};

interface IProps {
  parentLabel: string; // what is using the form builder. will be used to describe track event if enabled. capitalize. example 'Presets'
  defaultObject: Record<string, any>; // object with default values. used for resetting values when needed. eg: on parameter removal
  lists: Record<string, ISelectOption[]>; // all dropdowns and their associated options
  object: Record<string, any>; // the object that will be edited
  parameterCfg: Record<string, IParameterInfo>;
  parameterOptions: IParameterOption[];
  initialParameterKeys: string[]; // default active keys
  isLoading: boolean;
  customAddText?: string; // used if replacing "+ Add Parameter"
  activeParametersKey?: string; // use if there are multiple form builders in use for state separation eg: {supply: [], demand: []}
  errors: Record<string, string[]>; // use the parameter key to set an array of error strings
  enableTrackParameterSelect?: boolean; // use if you want to notify mixpanel what new parameter has been selected. uses parent label.
}

export const FormBuilder = ({
  isLoading,
  defaultObject,
  lists,
  object,
  parameterCfg,
  parameterOptions,
  initialParameterKeys,
  customAddText,
  activeParametersKey,
  errors = {},
  parentLabel,
  enableTrackParameterSelect = false,
}: IProps) => {
  const { user, isSuperUser } = useBaseStore();
  const [parameters, setParameters] = useState<IParameterInfo[]>(
    getInitialParameters(initialParameterKeys, parameterOptions, parameterCfg),
  );
  const [counter, setCounter] = useState(1);

  const handleAddParameter = () => {
    unstable_batchedUpdates(() => {
      setParameters((prevParams) => {
        const clonedParameters = cloneDeep(prevParams);
        clonedParameters.push({
          ...emptyParameterInfo,
          reactKey: counter,
        });
        return clonedParameters;
      });
      setCounter(counter + 1);
    });
  };

  const handleRemoveParameter = (idx: number): void => {
    setParameters((prevParams) => {
      const clonedParameters = cloneDeep(prevParams);
      clonedParameters.splice(idx, 1);
      return clonedParameters;
    });
  };

  const handleSelectParameter = (idx: number, key: string): void => {
    if (enableTrackParameterSelect) {
      track(
        `${parentLabel} - Select Parameter`,
        { isSuperUser, parameter: parameterCfg[key].label },
        user.user,
      );
    }
    setParameters((prevParams) => {
      const clonedParameters = cloneDeep(prevParams);
      const reactKey = clonedParameters[idx].reactKey;
      clonedParameters[idx] = parameterCfg[key];
      clonedParameters[idx].reactKey = reactKey;
      return clonedParameters;
    });
  };

  const activeParamKeys = extractActiveParamKeys(parameters);
  if (activeParametersKey) {
    object.ActiveParameters[activeParametersKey] = Array.from(activeParamKeys);
  } else {
    object.ActiveParameters = Array.from(activeParamKeys);
  }

  if (isLoading) return null;

  return (
    <FormBuilderContext.Provider
      value={{
        activeParamKeys,
        cfg: parameterCfg,
        defaultObject,
        handleRemoveParameter,
        handleSelectParameter,
        lists,
        object,
        options: parameterOptions,
      }}
    >
      <Rows
        gap={spacing.small}
        paddingBottom={spacing.small}
        style={{ minWidth: '800px', maxWidth: '960px' }}
      >
        {parameters.map((param, index) => {
          // if new/blank parameter, assign it a unique key
          const updatedKey = param.label || param.key + String(param.reactKey);

          return (
            <Row noPadding key={updatedKey}>
              <ParameterRow
                param={param}
                index={index}
                key={updatedKey}
                errors={errors[param.key] || []}
              />
            </Row>
          );
        })}
        <Row noHorizontalPadding smallVerticalPadding>
          <TextButton size='small' onClick={handleAddParameter}>
            {customAddText || '+ Add Parameter'}
          </TextButton>
        </Row>
      </Rows>
    </FormBuilderContext.Provider>
  );
};
